import React from 'react';
import { Pressable, ScrollView, Text, View, StyleSheet } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import Feather from '@expo/vector-icons/Feather';
import { useTranslation } from 'react-i18next';
import { dietLabels } from '../../../../utils/dietLabel';
import { Paprika } from '../../../../styles/theme';
import { TimeChip } from '../primitives/TimeChip';
import { SourceBadge } from '../primitives/SourceBadge';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import type { DietaryRole } from '../../../../redux/mealPlanSlice';
import { RemoteImage } from '../../../shared/RemoteImage';

interface Props {
  recipe: RecipeIndexEntry;
  dietaryRole?: DietaryRole;
  onClose: () => void;
  onRemove?: () => void;
  onShuffle?: () => void;
  /**
   * Open the move overlay. Desktop's only way to move a meal was dragging its
   * card, which touch hardware on the desktop layout can't do (#563 follow-up).
   */
  onMove?: () => void;
  /** Open the full standard recipe-detail page. */
  onExpand?: () => void;
  isCooked?: boolean;
  onCookedToggle?: () => void;
  /** Star rating 0-5; 0 means cooked but not yet rated. */
  rating?: number;
  onRate?: (rating: number) => void;
  /** Open the full "Comment c'était ?" capture (stars + date + note). */
  onRateRecipe?: () => void;
}

export function RecipeDetailDrawer({ recipe, onClose, onRemove, onShuffle, onMove, onExpand, isCooked, onCookedToggle, rating, onRateRecipe }: Props) {
  const { t } = useTranslation();
  return (
    <>
      <Pressable style={styles.scrim} onPress={onClose} />

      <View style={styles.drawer} testID="recipe-detail-drawer">
        <ScrollView style={styles.scroll} showsVerticalScrollIndicator={false}>
          {/* Photo */}
          <View style={styles.photoWrap}>
            <RemoteImage
              uri={recipe.image}
              style={styles.photo}
              resizeMode="cover"
              fallback={<View style={[styles.photo, styles.photoPlaceholder]} />}
            />
            <Pressable style={styles.closeBtn} onPress={onClose}>
              <Text style={styles.closeBtnText}>✕</Text>
            </Pressable>
          </View>

          {/* Info */}
          <View style={styles.content}>
            <Text style={styles.title}>{recipe.title}</Text>
            <View style={styles.metaRow}>
              <SourceBadge source={recipe.sourceUrl} />
              {recipe.readyInMinutes != null && <TimeChip minutes={recipe.readyInMinutes} />}
            </View>

            <View style={styles.tilesRow}>
              <MetaTile label={t('thisWeek.totalLabel')} value={recipe.readyInMinutes ? `${recipe.readyInMinutes} min` : '—'} />
              <MetaTile label={t('thisWeek.portionsLabel')} value={String(recipe.servings ?? 4)} />
            </View>

            {onExpand && (
              <Pressable style={styles.viewRecipeBtn} onPress={onExpand}>
                <Text style={styles.viewRecipeBtnText}>{t('thisWeek.viewFullRecipe')}</Text>
              </Pressable>
            )}

            {dietLabels(recipe.diets, t).length > 0 && (
              <View style={styles.dietsRow}>
                {dietLabels(recipe.diets, t).slice(0, 4).map((d) => (
                  <View key={d} style={styles.dietChip}>
                    <Text style={styles.dietChipText}>{d}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </ScrollView>

        {/* Footer */}
        <View style={styles.footer}>
          {/* Single cook CTA — opens the "Comment c'était ?" capture (marks
              cooked + rates + notes). Replaces the old "J'ai cuisiné ça" toggle. */}
          {onRateRecipe && (
            <Pressable style={styles.rateRecipeBtn} onPress={onRateRecipe} testID="meal-sheet-rate">
              <Ionicons name="star-outline" size={17} color={Paprika.primary} />
              <Text style={styles.rateRecipeBtnText}>{t('thisWeek.rateThisRecipe')}</Text>
            </Pressable>
          )}
          {isCooked && (
            <View style={styles.cookedMetaRow}>
              {(rating ?? 0) > 0 && (
                <View style={styles.starsRow}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Ionicons
                      key={star}
                      name={star <= (rating ?? 0) ? 'star' : 'star-outline'}
                      size={18}
                      color={star <= (rating ?? 0) ? '#F5C542' : Paprika.textMuted}
                      style={{ marginHorizontal: 1 }}
                    />
                  ))}
                </View>
              )}
              {onCookedToggle && (
                <Pressable onPress={onCookedToggle} hitSlop={6}>
                  <Text style={styles.unmarkLink}>{t('thisWeek.unmarkCooked')}</Text>
                </Pressable>
              )}
            </View>
          )}
          <View style={styles.actionsRow}>
            {onMove && (
              <Pressable style={styles.shuffleBtn} onPress={onMove} accessibilityRole="button">
                <Feather name="calendar" size={14} color={Paprika.textSecondary} />
                <Text style={styles.shuffleBtnText}>{t('thisWeek.move')}</Text>
              </Pressable>
            )}
            {onShuffle && (
              <Pressable style={styles.shuffleBtn} onPress={onShuffle} accessibilityRole="button">
                <Feather name="refresh-cw" size={14} color={Paprika.textSecondary} />
                <Text style={styles.shuffleBtnText}>{t('thisWeek.replace')}</Text>
              </Pressable>
            )}
            {onRemove && (
              <Pressable style={styles.removeBtn} onPress={onRemove}>
                <Text style={styles.removeBtnText}>{t('thisWeek.remove')}</Text>
              </Pressable>
            )}
          </View>
        </View>
      </View>
    </>
  );
}

function MetaTile({ label, value }: { label: string; value: string }) {
  return (
    <View style={tileStyles.tile}>
      <Text style={tileStyles.label}>{label}</Text>
      <Text style={tileStyles.value}>{value}</Text>
    </View>
  );
}

const tileStyles = StyleSheet.create({
  tile: {
    flex: 1,
    backgroundColor: Paprika.surfaceLow,
    borderRadius: 8,
    padding: 10,
    alignItems: 'center',
    gap: 2,
  },
  label: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
  },
  value: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 16,
    color: Paprika.textPrimary,
  },
});

const styles = StyleSheet.create({
  scrim: {
    position: 'absolute' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(27, 28, 26, 0.20)',
  },
  drawer: {
    position: 'absolute' as any,
    top: 0,
    right: 0,
    bottom: 0,
    width: 440,
    backgroundColor: Paprika.surfaceLowest,
    shadowColor: 'rgba(82, 68, 60, 0.14)',
    shadowOffset: { width: -4, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 24,
    zIndex: 65,
    flexDirection: 'column',
  },
  scroll: {
    flex: 1,
  },
  photoWrap: {
    height: 220,
    position: 'relative',
    backgroundColor: Paprika.surfaceLow,
  },
  photo: {
    width: '100%' as any,
    height: '100%' as any,
  },
  photoPlaceholder: {
    backgroundColor: Paprika.bgSunk,
  },
  closeBtn: {
    position: 'absolute' as any,
    top: 12,
    right: 12,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.88)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeBtnText: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.textSecondary,
  },
  content: {
    padding: 20,
    gap: 14,
  },
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 22,
    lineHeight: 28,
    color: Paprika.textPrimary,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  tilesRow: {
    flexDirection: 'row',
    gap: 8,
  },
  viewRecipeBtn: {
    backgroundColor: Paprika.primary,
    borderRadius: 100,
    paddingVertical: 12,
    alignItems: 'center',
  },
  viewRecipeBtnText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: '#fff',
  },
  dietsRow: {
    flexDirection: 'row',
    gap: 6,
    flexWrap: 'wrap',
  },
  dietChip: {
    backgroundColor: Paprika.bgSunk,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  dietChipText: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textSecondary,
  },
  footer: {
    flexDirection: 'column',
    gap: 10,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: Paprika.border,
  },
  rateRecipeBtn: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1.5,
    borderColor: Paprika.primary,
    borderRadius: 100,
    paddingVertical: 12,
  },
  rateRecipeBtnText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: Paprika.primary,
  },
  cookedMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  starsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  unmarkLink: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.textMuted,
    textDecorationLine: 'underline',
  },
  actionsRow: {
    flexDirection: 'row',
    gap: 10,
  },
  shuffleBtn: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
    borderWidth: 1.5,
    borderColor: Paprika.border,
    borderRadius: 100,
    paddingVertical: 12,
    alignItems: 'center',
  },
  shuffleBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  removeBtn: {
    paddingHorizontal: 16,
    borderWidth: 1.5,
    borderColor: Paprika.border,
    borderRadius: 100,
    paddingVertical: 12,
    alignItems: 'center',
  },
  removeBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textMuted,
  },
});
